<template>
  <div class="campanha-interna-popup-blocks">
    <template v-for="(block, idx) in normalizedBlocks">
      <div
        v-if="block.type === 'text'"
        :key="'t-' + idx"
        :class="['campanha-interna-popup-blocks__text', 'mb-2', alignTextClass(block), textSizeClass(block)]"
      >
        <strong v-if="block.title">{{ block.title }}</strong>
        <div v-if="block.body" class="campanha-interna-popup-blocks__body" v-html="sanitizeBody(block.body)"></div>
      </div>
      <div
        v-else-if="block.type === 'image'"
        :key="'i-' + idx"
        :class="['campanha-interna-popup-blocks__image', 'mb-2', alignTextClass(block)]"
      >
        <img
          :src="block.src"
          :alt="block.alt || ''"
          :class="imgAlignClass(block)"
          :style="imageInlineStyle(block)"
        />
      </div>
      <div
        v-else-if="block.type === 'video'"
        :key="'v-' + idx"
        :class="['campanha-interna-popup-blocks__video', 'mb-2', 'd-flex', flexRowJustify(block)]"
      >
        <div class="w-100" style="min-width: 0">
          <div v-if="youtubeEmbedUrl(block.url)" class="embed-responsive embed-responsive-16by9">
            <iframe
              class="embed-responsive-item"
              :src="youtubeEmbedUrl(block.url)"
              allowfullscreen
              title="Vídeo"
            />
          </div>
          <video v-else class="w-100 rounded" controls :src="block.url" />
        </div>
      </div>
      <div
        v-else-if="block.type === 'button'"
        :key="'b-' + idx"
        :class="['campanha-interna-popup-blocks__btn', 'mb-2', alignTextClass(block)]"
      >
        <a
          v-if="hasButtonHref(block)"
          :key="'popup-btn-' + idx + '-' + (block.icon || '') + '-' + (iconClasses(block) || '')"
          :href="block.href"
          target="_blank"
          rel="noopener noreferrer"
          :class="buttonClasses(block)"
          :style="buttonInlineStyle(block)"
          @click.prevent="onButtonClick(block, idx)"
        >
          <i v-if="iconClasses(block)" :class="iconClasses(block)" aria-hidden="true"></i>
          {{ block.label }}
        </a>
        <button
          v-else
          :key="'popup-btn-' + idx + '-nohref-' + (block.icon || '') + '-' + (iconClasses(block) || '')"
          type="button"
          :class="buttonClasses(block)"
          :style="buttonInlineStyle(block)"
          @click.prevent="onButtonClick(block, idx)"
        >
          <i v-if="iconClasses(block)" :class="iconClasses(block)" aria-hidden="true"></i>
          {{ block.label }}
        </button>
      </div>
      <div
        v-else-if="block.type === 'button_row'"
        :key="'br-' + idx"
        :class="['campanha-interna-popup-blocks__button-row', 'mb-2', 'd-flex', 'flex-wrap', 'align-items-center', buttonRowJustifyClass(block)]"
      >
        <template v-for="(btn, j) in (block.buttons || [])">
          <a
            v-if="hasButtonHref(btn)"
            :key="'brb-' + idx + '-' + j + '-' + (btn.icon || '') + '-' + (iconClasses(btn) || '')"
            :href="btn.href"
            target="_blank"
            rel="noopener noreferrer"
            :class="buttonRowItemClasses(btn)"
            :style="buttonInlineStyle(btn)"
            @click.prevent="onButtonClick(btn, idx, j)"
          >
            <i v-if="iconClasses(btn)" :class="iconClasses(btn)" aria-hidden="true"></i>
            {{ btn.label }}
          </a>
          <button
            v-else
            :key="'brb-' + idx + '-' + j + '-nohref-' + (btn.icon || '') + '-' + (iconClasses(btn) || '')"
            type="button"
            :class="buttonRowItemClasses(btn)"
            :style="buttonInlineStyle(btn)"
            @click.prevent="onButtonClick(btn, idx, j)"
          >
            <i v-if="iconClasses(btn)" :class="iconClasses(btn)" aria-hidden="true"></i>
            {{ btn.label }}
          </button>
        </template>
      </div>
      <div v-else-if="block.type === 'spacer'" :key="'s-' + idx" :style="{ height: (block.height || 16) + 'px' }" />
    </template>
  </div>
</template>

<script>
import axios from 'axios';

const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

function csrfToken() {
  const m = document.querySelector('meta[name="csrf-token"]');
  return m ? m.getAttribute('content') : '';
}

const BUTTON_VARIANTS = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'];

function expandHex3(hex) {
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return hex;
}

function luminanceRgba(hex) {
  const h = expandHex3(hex).slice(1);
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export default {
  name: 'CampanhaInternaPopupBlocks',
  props: {
    conteudo: {
      type: Object,
      default: () => ({}),
    },
    campanhaId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    normalizedBlocks() {
      const c = this.conteudo || {};
      return Array.isArray(c.blocks) ? c.blocks : [];
    },
  },
  mounted() {
    this.$nextTick(() => this.applyFontAwesomeSvg());
  },
  updated() {
    this.$nextTick(() => this.applyFontAwesomeSvg());
  },
  methods: {
    /**
     * O painel carrega Font Awesome 5 via SVG+JS (all.min.js). Ícones em HTML criados pelo Vue
     * (preview do formulário, etc.) não são substituídos automaticamente — precisamos de i2svg.
     */
    applyFontAwesomeSvg() {
      const el = this.$el;
      if (!el || typeof window === 'undefined') {
        return;
      }
      const fa = window.FontAwesome;
      if (fa && fa.dom && typeof fa.dom.i2svg === 'function') {
        fa.dom.i2svg({ node: el });
      }
    },
    hasButtonHref(block) {
      return (block.href || '').trim() !== '';
    },
    alignTextClass(block) {
      const a = block.align || 'left';
      if (a === 'center') return 'text-center';
      if (a === 'right') return 'text-right';
      return 'text-left';
    },
    textSizeClass(block) {
      const s = block.text_size || 'md';
      if (!['sm', 'md', 'lg', 'xl'].includes(s) || s === 'md') {
        return '';
      }
      return `campanha-interna-popup-blocks__text--${s}`;
    },
    flexRowJustify(block) {
      const a = block.align || 'left';
      if (a === 'center') return 'justify-content-center';
      if (a === 'right') return 'justify-content-end';
      return 'justify-content-start';
    },
    buttonRowJustifyClass(block) {
      return this.flexRowJustify(block);
    },
    imgAlignClass(block) {
      const a = block.align || 'left';
      const base = 'img-fluid rounded d-block';
      if (a === 'center') return `${base} mx-auto`;
      if (a === 'right') return `${base} ml-auto`;
      return base;
    },
    imageInlineStyle(block) {
      const w = block.image_max_width;
      if (w == null || w === '') {
        return {};
      }
      const n = Number(w);
      if (Number.isNaN(n) || n <= 0) {
        return {};
      }
      return {
        maxWidth: `${n}px`,
        width: '100%',
        height: 'auto',
      };
    },
    sanitizeBody(html) {
      if (!html) return '';
      return String(html).replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    },
    youtubeEmbedUrl(url) {
      if (!url || typeof url !== 'string') return '';
      const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
      if (m) {
        return `https://www.youtube.com/embed/${m[1]}`;
      }
      return '';
    },
    buttonRowItemClasses(btn) {
      return this.buttonClasses(btn).concat('campanha-interna-popup-blocks__button-row-item');
    },
    buttonClasses(block) {
      const classes = ['btn', 'btn-sm', 'btn-depth', 'campanha-interna-popup-blocks__btn-inner'];
      const hex = (block.custom_hex || '').trim();
      if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) {
        classes.push('campanha-interna-popup-blocks__btn--custom');
        return classes;
      }
      const v = block.variant && BUTTON_VARIANTS.includes(block.variant) ? block.variant : 'primary';
      if (block.outline) {
        classes.push(`btn-outline-${v}`);
      } else {
        classes.push(`btn-${v}`);
      }
      return classes;
    },
    buttonInlineStyle(block) {
      const hex = (block.custom_hex || '').trim();
      if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) {
        return {};
      }
      const expanded = expandHex3(hex);
      const lum = luminanceRgba(expanded);
      const fg = lum > 0.55 ? '#212529' : '#fff';
      return {
        backgroundColor: expanded,
        borderColor: expanded,
        color: fg,
      };
    },
    iconClasses(block) {
      const raw = (block.icon || '').trim();
      if (!raw) {
        return null;
      }
      const parts = raw.split(/\s+/).filter(Boolean);
      const stylePrefixes = ['fas', 'far', 'fab', 'fal', 'fad'];

      // Já no formato FA5+: "fas fa-thumbs-up", "far fa-star", …
      if (parts.length >= 2 && stylePrefixes.includes(parts[0])) {
        return parts.join(' ');
      }

      // FA4: "fa fa-thumbs-up" → "fas fa-thumbs-up"
      if (parts.length === 2 && parts[0] === 'fa' && parts[1].startsWith('fa-')) {
        return `fas ${parts[1]}`;
      }

      // "fa thumbs-up" (nome sem prefixo fa- no segundo token)
      if (parts.length === 2 && parts[0] === 'fa' && parts[1] && !parts[1].startsWith('fa-')) {
        return `fas fa-${parts[1].replace(/^fa-/, '')}`;
      }

      // Só "fa-thumbs-up" — precisa do estilo "fas" (antes virava classe inválida)
      if (parts.length === 1 && parts[0].startsWith('fa-')) {
        return `fas ${parts[0]}`;
      }

      // Nome curto: "thumbs-up", "arrow-right"
      if (!parts.some((p) => p.startsWith('fa-'))) {
        const name = parts.join('-');
        return `fas fa-${name.replace(/^fa-/, '')}`;
      }

      const faNamed = parts.find((p) => p.startsWith('fa-'));
      if (faNamed) {
        return `fas ${faNamed}`;
      }

      return `fas fa-${raw.replace(/^fa-/, '')}`;
    },
    async onButtonClick(block, blockIndex, subIndex) {
      const href = (block.href || '').trim();
      const id = this.campanhaId != null && this.campanhaId !== '' ? Number(this.campanhaId) : null;

      if (href) {
        const w = window.open(href, '_blank');
        if (w) {
          w.opener = null;
        }
      }

      if (id != null && !Number.isNaN(id)) {
        try {
          const base = (window.baseUrl || '').replace(/\/$/, '');
          const token = csrfToken();
          const body = {
            campanha_interna_id: id,
            block_index: blockIndex,
            label: block.label,
            href,
          };
          if (subIndex !== undefined && subIndex !== null) {
            body.button_sub_index = subIndex;
          }
          await axios.post(`${base}/painel/campanha-internas/popup/click`, body, {
            headers: {
              ...jsonHeaders,
              ...(token ? { 'X-CSRF-TOKEN': token } : {}),
            },
          });
        } catch (e) {
          console.error(e);
        }
      }

      this.$emit('close');
    },
  },
};
</script>
